import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Star } from 'lucide-react'
import { useAnchoredPopover } from '../hooks/useAnchoredPopover'

interface StarRatingProps {
    /** Current rating 1–5, or null/0 for unrated. */
    value: number | null
    /** Set the rating. Clicking the current top star again clears it (passes 0). Omit for read-only. */
    onChange?: (rating: number) => void
    size?: number
    /** Stop row-click propagation (when embedded in a clickable table row). */
    stopPropagation?: boolean
}

/** Interactive 1–5 star rating. Reuses lucide's Star (filled amber when active). Hover previews the value;
 *  clicking sets it, and clicking the current rating's top star again clears it. Read-only when no onChange. */
export const StarRating = ({ value, onChange, size = 15, stopPropagation }: StarRatingProps) => {
    const [hover, setHover] = useState<number | null>(null)
    const current = value ?? 0
    const shown = hover ?? current
    const readOnly = !onChange

    return (
        <div className="inline-flex items-center gap-0.5" onClick={stopPropagation ? (e) => e.stopPropagation() : undefined} role="presentation">
            {[1, 2, 3, 4, 5].map((star) => {
                const active = star <= shown
                return (
                    <button
                        key={star}
                        type="button"
                        disabled={readOnly}
                        aria-label={`${star} star${star > 1 ? 's' : ''}`}
                        onMouseEnter={readOnly ? undefined : () => setHover(star)}
                        onMouseLeave={readOnly ? undefined : () => setHover(null)}
                        onClick={
                            readOnly
                                ? undefined
                                : (e) => {
                                      e.stopPropagation()
                                      onChange!(star === current ? 0 : star)
                                  }
                        }
                        className={`border-none bg-transparent p-0 leading-none ${readOnly ? '' : 'cursor-pointer'} ${active ? 'text-warning' : 'text-foreground-subtle'}`}
                    >
                        <Star size={size} fill={active ? 'currentColor' : 'none'} />
                    </button>
                )
            })}
        </div>
    )
}

/**
 * Collapsed rating for tight table cells: ONE star (amber-filled when rated) plus the star count as a small
 * badge — a five-star row per row is what made the Rating column the widest non-text column on small
 * screens. Clicking opens an anchored popover with the full interactive StarRating, so inline rating still
 * works; the popover closes on pick. Read-only (no onChange) just displays.
 */
export const CompactStarRating = ({ value, onChange, size = 15 }: Omit<StarRatingProps, 'stopPropagation'>) => {
    const { open, anchorRect, containerRef, triggerRef, panelRef, close, togglePanel, panelPositionStyle } = useAnchoredPopover()
    const current = value ?? 0
    const rated = current > 0

    return (
        <div ref={containerRef} className="inline-flex" onClick={(e) => e.stopPropagation()} role="presentation">
            <button
                ref={triggerRef}
                type="button"
                disabled={!onChange}
                aria-label={rated ? `Rated ${current} of 5` : 'Rate this load'}
                title={rated ? `${current} / 5` : onChange ? 'Rate this load' : undefined}
                onClick={onChange ? togglePanel : undefined}
                className={`inline-flex items-center gap-1 rounded border-none bg-transparent p-0 leading-none ${onChange ? 'cursor-pointer' : ''} ${rated ? 'text-warning' : 'text-foreground-subtle'}`}
            >
                <Star size={size} fill={rated ? 'currentColor' : 'none'} />
                {rated && <span className="min-w-[16px] rounded-full bg-default px-1 text-[10px] font-bold leading-4 text-foreground">{current}</span>}
            </button>
            {open &&
                anchorRect &&
                createPortal(
                    <div
                        ref={panelRef}
                        // The trigger is a tiny star, so ignore the hook's trigger-width sizing.
                        style={{ ...panelPositionStyle, width: 'auto' }}
                        className="rounded-2xl bg-surface p-2 shadow-overlay"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <StarRating
                            value={value}
                            size={18}
                            onChange={(rating) => {
                                onChange?.(rating)
                                close()
                            }}
                        />
                    </div>,
                    document.body
                )}
        </div>
    )
}
